import type { BoxProps } from '@mui/material/Box';

import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';

import { varContainer } from './variants';

// ----------------------------------------------------------------------

/**
 * 스크롤이 닿으면 안의 것을 차례로 띄우는 껍데기. `varContainer()` 를 물려 자식의
 * `varFade` 들이 순서대로 실행된다.
 *
 * ⚠️ 기본값 없는 프롭은 **하나도 없다** — `<MotionViewport>{children}</MotionViewport>` 만으로 선다.
 *
 * ⚠️ **`disableAnimate` 의 기본값이 `true` 다.** 그래서 `sm` 아래(모바일)에서는 애니메이션이
 * 통째로 꺼지고 그냥 `Box` 가 된다. 모바일에서 「안 뜬다」는 것은 고장이 아니다.
 *
 * ⚠️ `viewport` 는 통째로 갈아 끼우지 않고 **기본값에 얹는다** —
 * `{ once: true, amount: 0.3, ...viewport }`. `once` 라 한 번 뜨면 다시 숨지 않는다.
 *
 * ⚠️ 나머지 프롭은 `Box` 로 그대로 간다. 그래서 `<Container component={MotionViewport} sx={…}>`
 * 처럼 다른 컴포넌트의 `component` 로 얹어 쓸 수 있다(`sections/examples` 의 animate 데모).
 */
export type MotionViewportProps = Omit<BoxProps, 'component'> & {
  /** framer-motion 의 `whileInView` 판정. 기본값에 얹힌다 */
  viewport?: { once?: boolean; amount?: number | 'some' | 'all'; margin?: string };
  /** 모바일에서 애니메이션을 끌지. 기본 `true` */
  disableAnimate?: boolean;
};

export function MotionViewport({
  children,
  viewport,
  disableAnimate = true,
  ...other
}: MotionViewportProps) {
  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const disabled = smDown && disableAnimate;

  const baseProps = disabled
    ? {}
    : {
        component: m.div,
        initial: 'initial',
        whileInView: 'animate',
        variants: varContainer(),
        viewport: { once: true, amount: 0.3, ...viewport },
      };

  return (
    <Box {...baseProps} {...other}>
      {children}
    </Box>
  );
}
